/**
 * The live room's two settings (owner rulings R11, R12, R19, 2026-10-28):
 * what happens when the host's timer reaches zero, and how names are shown.
 * Set by the workflow, then the cycle, then the track, then the session, each
 * able to override the one above. Stored in session_policies.timer_at_zero and
 * session_policies.anonymity; the server reads the same rows
 * (internal_session_room_rules, migration 973).
 */
import { SessionPolicySetting, type SessionPolicySettingProps } from './SessionPolicySetting';
import type { RoomAnonymity, TimerAtZero } from '@/stores/prioritization/sessionPolicyStore';

export type RoomRulesSettingsProps = Pick<
  SessionPolicySettingProps<string>,
  'scopeKind' | 'scopeId' | 'ids' | 'canEdit' | 'onSaved'
>;

const TIMER_AT_ZERO_WORDS: Record<TimerAtZero, string> = {
  tell: 'The host is told',
  lock: 'Answers lock',
};

const ANONYMITY_WORDS: Record<RoomAnonymity, string> = {
  shown: 'Names shown',
  hidden: 'Names hidden on every screen',
  withheld: 'Names withheld from the data too',
};

export function RoomRulesSettings(props: RoomRulesSettingsProps) {
  return (
    <>
      <SessionPolicySetting<TimerAtZero>
        {...props}
        field="timerAtZero"
        label="Timer at zero"
        ariaLabel="What happens when the room's timer reaches zero"
        choices={[
          { value: 'tell', label: TIMER_AT_ZERO_WORDS.tell },
          { value: 'lock', label: TIMER_AT_ZERO_WORDS.lock },
        ]}
        fallback="tell"
        describe={(v) =>
          v === 'lock'
            ? 'When the host’s timer reaches zero, no answer can be given or changed.'
            : 'When the host’s timer reaches zero, the host is told; answers are still taken.'
        }
        testId="timer-at-zero-setting"
      />
      <SessionPolicySetting<RoomAnonymity>
        {...props}
        field="anonymity"
        label="Names"
        ariaLabel="How the session shows who said what"
        choices={[
          { value: 'shown', label: ANONYMITY_WORDS.shown },
          { value: 'hidden', label: ANONYMITY_WORDS.hidden },
          { value: 'withheld', label: ANONYMITY_WORDS.withheld },
        ]}
        fallback="shown"
        describe={(v) =>
          v === 'shown'
            ? 'Everyone sees who is in the room and who has answered.'
            : v === 'hidden'
              ? 'No screen names anyone, the host’s included; the room shows counts. Admins still see names in the record.'
              : 'As hidden, and each answer is saved without the person’s name; only a workspace admin can see who gave it.'
        }
        testId="anonymity-setting"
      />
    </>
  );
}
